import React, { useState, useEffect } from 'react';
import { Button, Input, Popconfirm, Space, Table, TableColumnsType, Tag, message } from 'antd';
import { useRequest } from 'ahooks';
import dayjs from 'dayjs';
import {
  getPromptTemplateList,
  getPromptVersionList,
  deletePromptTemplate,
  deletePromptVersion,
  switchPromptVersion,
  PromptTemplateVo,
  PromptVersionVo,
} from '@/api/prompt';
import { useUpdate } from '@/common/hook';
import EditTemplate from './editTemplateModal';
import EditVersion from './editVersionModal';
import CompareVersion from './compareVersionModal';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';

const templateColumns: TableColumnsType<PromptTemplateVo> = [
  { title: '模板名称', dataIndex: 'name', key: 'name', width: 150 },
  { title: '模板描述', dataIndex: 'description', key: 'description', width: 200, ellipsis: true },
  {
    title: '当前版本',
    dataIndex: 'currentVersion',
    key: 'currentVersion',
    width: 100,
    render: (version: string) => version ? <Tag color="blue">{version}</Tag> : <Tag>无版本</Tag>,
  },
  {
    title: '创建人',
    dataIndex: 'createdBy',
    key: 'createdBy',
    width: 100,
  },
  {
    title: '创建时间',
    dataIndex: 'createdAt',
    key: 'createdAt',
    width: 180,
    render: text => text ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : '-',
  },
  {
    title: '更新时间',
    dataIndex: 'updatedAt',
    key: 'updatedAt',
    width: 180,
    render: text => text ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : '-',
  },
];

const versionColumns: TableColumnsType<PromptVersionVo> = [
  { title: '版本号', dataIndex: 'version', key: 'version', width: 100 },
  {
    title: '变更说明',
    dataIndex: 'changeLog',
    key: 'changeLog',
    ellipsis: true,
  },
  {
    title: '创建人',
    dataIndex: 'createdBy',
    key: 'createdBy',
    width: 100,
  },
  {
    title: '创建时间',
    dataIndex: 'createdAt',
    key: 'createdAt',
    width: 180,
    render: text => text ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : '-',
  },
  {
    title: '更新人',
    dataIndex: 'updatedBy',
    key: 'updatedBy',
    width: 100,
  },
  {
    title: '更新时间',
    dataIndex: 'updatedAt',
    key: 'updatedAt',
    width: 180,
    render: text => text ? dayjs(text).format('YYYY-MM-DD HH:mm:ss') : '-',
  },
];

const PromptTemplateList = () => {
  const [editData, setEditData] = useState<PromptTemplateVo>();
  const [editVisible, setEditVisible] = useState<boolean>(false);
  const [keyword, setKeyword] = useState<string>('');
  const [update, updateFn] = useUpdate();

  // 版本相关状态
  const [versionData, setVersionData] = useState<PromptVersionVo>();
  const [versionVisible, setVersionVisible] = useState<boolean>(false);
  const [currentTemplateId, setCurrentTemplateId] = useState<number>(0);

  // 对比弹窗状态
  const [compareVisible, setCompareVisible] = useState<boolean>(false);
  const [compareFromId, setCompareFromId] = useState<number>(0);
  const [compareToId, setCompareToId] = useState<number>(0);

  // 展开行相关状态
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  const [versionMap, setVersionMap] = useState<Map<number, PromptVersionVo[]>>(new Map());

  const { data: templateList, loading } = useRequest(() => getPromptTemplateList({ keyword }), {
    refreshDeps: [update, keyword],
  });

  // 当展开行变化时，加载对应模板的版本列表
  useEffect(() => {
    expandedKeys.forEach(key => {
      const templateId = Number(key);
      if (!versionMap.has(templateId)) {
        getPromptVersionList(templateId).then(list => {
          setVersionMap(prev => new Map(prev).set(templateId, list || []));
        });
      }
    });
  }, [expandedKeys]);

  const onDeleteTemplate = (id: number) => {
    deletePromptTemplate(id).then(() => {
      message.success('删除成功');
      updateFn();
      setVersionMap(prev => {
        const newMap = new Map(prev);
        newMap.delete(id);
        return newMap;
      });
    });
  };

  const onDeleteVersion = (templateId: number, versionId: number) => {
    deletePromptVersion(versionId).then(() => {
      message.success('删除成功');
      getPromptVersionList(templateId).then(list => {
        setVersionMap(prev => new Map(prev).set(templateId, list || []));
      });
    });
  };

  const handleCompare = (fromId: number, toId: number) => {
    setCompareFromId(fromId);
    setCompareToId(toId);
    setCompareVisible(true);
  };

  const handleSwitchVersion = (templateId: number, versionId: number) => {
    switchPromptVersion({ templateId, versionId }).then(() => {
      message.success('版本切换成功');
      updateFn();
    });
  };

  const handleExpand = (expanded: boolean, record: PromptTemplateVo) => {
    if (expanded) {
      setExpandedKeys([...expandedKeys, record.id]);
    } else {
      setExpandedKeys(expandedKeys.filter(key => key !== record.id));
    }
  };

  const buildColumns = [
    ...templateColumns,
    {
      title: '操作',
      key: 'operation',
      width: 100,
      render: (_: any, record: PromptTemplateVo) => (
        <Space size={8}>
          <Button
            type="link"
            size="small"
            style={{ padding: 0 }}
            onClick={() => {
              setEditData(record);
              setEditVisible(true);
            }}
          >
            编辑
          </Button>
          <Popconfirm title="确定删除该模板及其所有版本？" onConfirm={() => onDeleteTemplate(record.id)}>
            <Button type="link" size="small" style={{ padding: 0 }} danger>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const expandedRowRender = (template: PromptTemplateVo) => {
    const versionList = versionMap.get(template.id) || [];

    const versionActionColumns = [
      ...versionColumns,
      {
        title: '操作',
        key: 'operation',
        width: 200,
        render: (_: any, record: PromptVersionVo, index: number) => (
          <Space size={4}>
            <Button
              type="link"
              size="small"
              style={{ padding: 0 }}
              onClick={() => {
                setCurrentTemplateId(template.id);
                setVersionData(record);
                setVersionVisible(true);
              }}
            >
              编辑
            </Button>
            <Button
              type="link"
              size="small"
              style={{ padding: 0 }}
              onClick={() => handleSwitchVersion(template.id, record.id)}
            >
              应用
            </Button>
            <Popconfirm title="确定删除该版本？" onConfirm={() => onDeleteVersion(template.id, record.id)}>
              <Button type="link" size="small" style={{ padding: 0 }} danger>
                删除
              </Button>
            </Popconfirm>
          </Space>
        ),
      },
    ];

    return (
      <div style={{ padding: '12px 24px', background: '#fafafa' }}>
        <div style={{ marginBottom: 12 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => {
            setCurrentTemplateId(template.id);
            setVersionData(undefined);
            setVersionVisible(true);
          }}>
            新增版本
          </Button>
        </div>
        <Table<PromptVersionVo>
          columns={versionActionColumns}
          dataSource={versionList}
          pagination={false}
          rowKey="id"
          size="small"
          loading={!versionMap.has(template.id)}
        />
      </div>
    );
  };

  return (
    <>
      <div className="flex gap-3 mb-3">
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setEditVisible(true)}>
          新增模板
        </Button>
        <Input
          placeholder="搜索模板名称"
          prefix={<SearchOutlined />}
          value={keyword}
          onChange={e => setKeyword(e.target.value)}
          style={{ width: 300 }}
          allowClear
        />
      </div>
      <Table<PromptTemplateVo>
        columns={buildColumns}
        dataSource={templateList ?? []}
        loading={loading}
        rowKey="id"
        expandable={{
          expandedRowRender,
          expandedRowKeys: expandedKeys,
          onExpand: handleExpand,
          expandIcon: ({ expanded, onExpand, record }) => (
            <span
              onClick={e => {
                e.stopPropagation();
                onExpand(record, e);
              }}
              style={{ cursor: 'pointer', padding: '4px 8px' }}
            >
              {expanded ? '▼' : '▶'}
            </span>
          ),
        }}
        onRow={(record) => ({
          onClick: () => handleExpand(!expandedKeys.includes(record.id), record),
          style: { cursor: 'pointer' },
        })}
      />
      {editVisible && (
        <EditTemplate
          data={editData}
          isEdit={!!editData}
          updateFn={updateFn}
          onCancel={() => {
            setEditVisible(false);
            setEditData(undefined);
          }}
        />
      )}
      {versionVisible && (
        <EditVersion
          templateId={currentTemplateId}
          data={versionData}
          isEdit={!!versionData}
          isFirstVersion={!versionMap.has(currentTemplateId) || (versionMap.get(currentTemplateId)?.length === 0)}
          updateFn={() => {
            updateFn();
            getPromptVersionList(currentTemplateId).then(list => {
              setVersionMap(prev => new Map(prev).set(currentTemplateId, list || []));
            });
          }}
          onCancel={() => {
            setVersionVisible(false);
            setVersionData(undefined);
          }}
        />
      )}
      {compareVisible && (
        <CompareVersion
          fromVersionId={compareFromId}
          toVersionId={compareToId}
          onCancel={() => setCompareVisible(false)}
        />
      )}
    </>
  );
};

export default PromptTemplateList;